import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { RestApiModel } from '../../../shared/model/rest-api.model';

@Injectable()
export class KeywordService {
  private readonly PRODUCT_ID_KEY = '{product_id}';
  private readonly KEYWORDS_URL =
    RestApiModel.PROXY_API_URL + `/product/${this.PRODUCT_ID_KEY}/keyword`;

  constructor(private http: HttpClient) {}

  /**
   * GET - Keyword associated to a product.
   * @param productId
   */
  getKeywords(productId: number): Observable<any> {
    return this.http
      .get(this.KEYWORDS_URL.replace(this.PRODUCT_ID_KEY, productId.toString()))
      .pipe(
        map((res: any) => res),
        catchError((error: any) => error)
      );
  }

  /**
   * POST - Associates a keyword to a product.
   * @param productId
   * @param keyword
   * @param keywordId
   */

  createKeyword(productId: number, keyword: string): Observable<any> {
    const request = {
      keyword: keyword
    };
    return this.http
      .post(
        this.KEYWORDS_URL.replace(this.PRODUCT_ID_KEY, productId.toString()),
        request
      )
      .pipe(
        map((res: any) => {
          if (
            res.errorCode !== 132 &&
            (res.responseCode === 400 ||
              (res.errorMessage && res.errorMessage.length > 0))
          ) {
            return {
              status: 'failed',
              productId: productId
            };
          } else {
            return res;
          }
        }),
        catchError((error: any) =>
          of({
            status: 'failed',
            productId: productId,
            error: error.message
          })
        )
      );
  }
  /**
   * PUT - Edits a keyword associated to a product.
   * @param productId
   * @param keyword
   * @param keywordId
   */
  updateKeyword(
    productId: number,
    keyword: string,
    keywordId: number
  ): Observable<any> {
    const request = {
      keyword: keyword
    };
    return this.http
      .put(
        this.KEYWORDS_URL.replace(
          this.PRODUCT_ID_KEY,
          productId.toString()
        ).concat(`/${keywordId}`),
        request
      )
      .pipe(
        map((res: any) => res),
        catchError((error: any) => error)
      );
  }

  /**
   * Create the Observables Array of POST associations for product make & model.
   * @param productToBeDeactivated
   * @param activeProductId
   */
  createKeywordAssociationForProductDeactivation(
    productToBeDeactivated: Array<any>,
    activeProductIds: Array<number>
  ): Observable<any> {
    let associations = new Array<Observable<any>>();
    productToBeDeactivated.forEach(product => {
      // for each product it will create N # of keyword associations.
      activeProductIds.forEach(id => {
        const make: string = product.make;
        const model: string = product.model;
        // One keyword for Make
        if (make && make.length > 0) {
          associations.push(this.createKeyword(id, make));
        }
        // One keyword for Model
        if (model && model.length > 0) {
          associations.push(this.createKeyword(id, model));
        }
      });
    });
    return forkJoin(associations);
  }

  /**
   * Build the error message for the failed keyword associations.
   * @param productIds: Array<numbers>
   * @param products: Array<any>
   */
  buildKeywordAssociationErrorMessage(
    productIds: Array<number>,
    products: Array<any>
  ): string {
    let result =
      'A system error prevented the keyword from being added to the following Products: [';
    productIds.forEach((id, index) => {
      const match = products.find(product => product.id === id);
      if (match) {
        result = result.concat(
          `${match.model}${index === productIds.length - 1 ? '' : ', '}`
        );
      }
    });
    result = result.concat(
      ']. Please add the keywords manually in each Product Details page.'
    );
    return result;
  }
}
